import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { getExamSlotsForUser } from '@ils/slots/api/slot.js';
import type { Slot } from '@ils/slots/db/slot.js';
import { html, type PropertyValueMap } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { distinctUntilChanged, filter, map, of, switchMap } from 'rxjs';
import { combineLatest } from 'rxjs/internal/observable/combineLatest';
import type { ILSUser } from '../../../db/ils-user.js';
import { ServerTime } from '../../../db/time.js';
import { ILSVideoChat } from './ils-video-chat.js';

const slotSelector = {
  _id: 1,
  _class: 1,
  startTime: 1,
  endTime: 1,
} as const;

@customElement('ils-video-check-in')
export class IlsVideoCheckIn extends ILSVideoChat {
  @state() examDayID = new RXController(this, routingState.searchParams.get('day').pipe(distinctUntilChanged()));
  private readonly _serverTime = new RXController(this, ServerTime.subscribeServerTime()());
  protected _currentUser = new RXController(this, currentUser<ILSUser>().pipe(filter(Boolean)));
  @state() public examSlots = new RXController(
    this,
    routingState.searchParams.pipe(
      switchMap(params => {
        const day = params.get('day');
        if (day) return getExamSlotsForUser(day)(slotSelector);
        return of([] as Slot[]);
      }),
      map(slots => {
        return slots.sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis());
      }),
    ),
  );
  @state() private readonly _timeTillSlot = new RXController(
    this,
    combineLatest([this.examSlots.observable, this._serverTime.observable]).pipe(
      filter(([slots, serverTime]) => !!slots?.length && !!serverTime),
      map(([slots, serverTime]) => {
        const firstSlotStart = slots![0]!.startTime;
        const time = DateTime.fromJSDate(serverTime!);
        const diff = firstSlotStart.diff(time);
        return diff;
      }),
    ),
  );

  override statusDisplay() {
    if (!this._timeTillSlot.value) return html``;
    return html`
      <x-basic-card padding="md" ${css({ position: 'absolute', top: '1em', left: '1em' })}>
        <x-flex horizontal space="sm">
          <x-flex horizontal crossaxis-center start space="sm">
            <x-button
              mode="icon"
              icon="arrow_back"
              @click=${() => {
                try {
                  window.history.back();
                } catch {
                  goTo({ path: '/start/scheduling', searchParams: NO_CHANGE });
                }
              }}
            ></x-button>
            <x-h3>
              Prüfung beginnt in:
              <b>
                ${this._timeTillSlot.value.as('minutes') > 0
                  ? this._timeTillSlot.value.toFormat('mm:ss')
                  : 'Läuft bereits!'}
              </b>
            </x-h3>
          </x-flex>
          <x-button
            mode=${this._timeTillSlot.value.as('minutes') < 5 ? 'action' : 'outline'}
            @click=${() => {
              goTo({
                path: '/start/exam',
                searchParams: NO_CHANGE,
              });
            }}
          >
            Zur Prüfung
          </x-button>
        </x-flex>
      </x-basic-card>
    `;
  }

  protected override willUpdate(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    super.willUpdate(_changedProperties);
    if (_changedProperties.has('examDayID') && this.examDayID.value) this._joinRoom(this.examDayID.value);
  }
}
